import { getBackgroundViewport, type DynamicZoomBackgroundMotion, type DynamicZoomViewport } from './dynamicZoom';
import { getFramedCameraRect, type StaticFraming } from './staticFraming';

interface CameraLayers {
  background: CanvasImageSource;
  /** Opt-in procedural glass: sample the background after its own camera transform. */
  beforeForeground?: (ctx:CanvasRenderingContext2D,foregroundRect:ReturnType<typeof getFramedCameraRect>,backgroundRect:ReturnType<typeof getFramedCameraRect>)=>void;
}

// Lifetime follows the existing master cache; no global accumulation of 4K layers.
const layers = new WeakMap<HTMLCanvasElement, CameraLayers>();

export function setDynamicZoomLayers(master: HTMLCanvasElement, value: CameraLayers): void {
  layers.set(master, value);
}

/** Shared by the main preview, animation editor and final MP4 composition.
 * Cropping cached layers needs no screen warp or mesh recalculation. */
export function drawDynamicZoomFrame(
  ctx: CanvasRenderingContext2D,
  master: HTMLCanvasElement,
  viewport: DynamicZoomViewport,
  framing: StaticFraming,
  mode: DynamicZoomBackgroundMotion = 'stick'
): void {
  const separated = mode === 'stick' ? undefined : layers.get(master);
  const draw = (source: CanvasImageSource, camera: DynamicZoomViewport) => {
    // Prepared backgrounds are square composition canvases, just like master.
    const width = 'width' in source ? Number(source.width) : master.width;
    const height = 'height' in source ? Number(source.height) : master.height;
    const rect = getFramedCameraRect(camera, width, height, framing);
    ctx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, ctx.canvas.width, ctx.canvas.height);
    return rect;
  };
  if (separated) {
    const backgroundRect=draw(separated.background, getBackgroundViewport(viewport, mode));
    separated.beforeForeground?.(ctx,getFramedCameraRect(viewport,master.width,master.height,framing),backgroundRect);
    draw(master, viewport);
  } else {
    draw(master, viewport);
  }
}
